import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MatchTeam } from './factions'

/**
 * Strateji editöründeki takımların kaynağı: stratejinin bağlı olduğu maçın kadrosu.
 *
 * Bir simgeye ya da oka atanan takım kadro takımının **kimliğini** taşır, ad ve
 * oyuncular kayıtta tutulmaz, ekrana çizilirken kadrodan okunur. Böylece kadroda
 * adı ya da oyuncuları değişen takım, slaytlara dokunmadan her yerde değişir;
 * kadrodan silinen takım simgede görünmez olur.
 */

export interface RosterPlayer {
  id: string
  name: string
  avatar_url: string
}

export interface RosterSquad {
  id: string
  name: string
  color: string
  players: RosterPlayer[]
}

export interface AssignedSquad {
  /** Kadro takımının kimliği. Elle yazılmış takımda yok. */
  id?: string
  name: string
  color: string
}

/** Kadroda takım rengi yok. Renk atanırken sırayla verilir, sonra simgede durur. */
const SQUAD_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#a855f7', '#06b6d4', '#ec4899', '#84cc16']

/** Yönetim kadroyu değiştirince açık editörlerin bunu ne kadar sürede göreceği. */
const POLL_MS = 20_000

const DEFAULT_AVATAR = '/default-avatar.png'

interface SquadsResponse {
  squads?: { id: string; name: string; slots?: { userId?: string | null }[] }[]
  custom_players?: { user_id: string; name: string; avatar_url?: string | null }[]
}

interface MemberRow {
  id: string
  username: string
  display_name: string
  avatar_url: string
}

/**
 * Maçın kadro takımları. `null`: maç yok ya da kadro henüz okunmadı; bu durumda
 * atanmış takımlara dokunulmaz, yoksa ilk yükleme anında hepsi silinmiş görünürdü.
 */
export function useMatchRoster(eventId: string | undefined): RosterSquad[] | null {
  const [squads, setSquads] = useState<RosterSquad[] | null>(null)
  const lastJson = useRef('')

  useEffect(() => {
    setSquads(null)
    lastJson.current = ''
    if (!eventId) return
    let stopped = false
    let members: Map<string, MemberRow> | null = null

    const load = async () => {
      try {
        if (!members) {
          const res = await fetch('/api/members', { credentials: 'include' })
          const rows: MemberRow[] = res.ok ? await res.json() : []
          members = new Map(rows.map(m => [m.id, m]))
        }
        const res = await fetch(`/api/matches/${encodeURIComponent(eventId)}/squads`, { credentials: 'include' })
        // Okunamadıysa son bilinen kadro kalır: kaçan tek bir yoklama takımları düşürmesin.
        if (!res.ok || stopped) return
        const data: SquadsResponse = await res.json()
        if (stopped) return
        const custom = new Map((data.custom_players ?? []).map(p => [p.user_id, p]))
        const next: RosterSquad[] = (data.squads ?? []).map((sq, i) => ({
          id: sq.id,
          name: sq.name,
          color: SQUAD_COLORS[i % SQUAD_COLORS.length],
          players: (sq.slots ?? []).flatMap(slot => {
            const id = slot.userId
            if (!id) return []
            const member = members?.get(id)
            const guest = custom.get(id)
            return [{
              id,
              name: member?.display_name || member?.username || guest?.name || id,
              avatar_url: member?.avatar_url || guest?.avatar_url || DEFAULT_AVATAR,
            }]
          }),
        }))
        // Editör çok büyük: değişmeyen yanıt onu yeniden çizdirmesin.
        const json = JSON.stringify(next)
        if (json !== lastJson.current) {
          lastJson.current = json
          setSquads(next)
        }
      } catch { /* ağ hatası: bir sonraki yoklama dener */ }
    }

    void load()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, POLL_MS)
    const onFocus = () => void load()
    window.addEventListener('focus', onFocus)
    return () => {
      stopped = true
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
    }
  }, [eventId])

  return squads
}

/**
 * Bağlı maçın takımları ve tarafları. Tek maçı veren bir uç yok, maç listesinden
 * okunur; editör açılışında bir kez yeterli, taraf oyun sırasında değişmiyor.
 */
export function useMatchTeams(eventId: string | undefined): MatchTeam[] {
  const [teams, setTeams] = useState<MatchTeam[]>([])

  useEffect(() => {
    setTeams([])
    if (!eventId) return
    let stopped = false
    fetch('/api/matches', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : []))
      .then((rows: { event_id: string; event?: { teams?: MatchTeam[] } }[]) => {
        if (stopped) return
        setTeams(rows.find(m => m.event_id === eventId)?.event?.teams ?? [])
      })
      .catch(() => {})
    return () => { stopped = true }
  }, [eventId])

  return teams
}

/** Atanmış takımın kadrodaki karşılığı; elle yazılmış ad kadroda birebir varsa ona bağlanır. */
function findRosterSquad(squad: AssignedSquad, roster: RosterSquad[]): RosterSquad | undefined {
  if (squad.id) return roster.find(r => r.id === squad.id)
  const byName = roster.filter(r => r.name === squad.name)
  return byName.length === 1 ? byName[0] : undefined
}

/**
 * Atanmış takımların güncel hâli ve onlara bağlı oyuncular. Kayda yazılmaz, yalnızca
 * ekrana çizilirken kullanılır.
 */
export function useLiveSquads(roster: RosterSquad[] | null) {
  const liveSquads = useCallback(<T extends AssignedSquad>(list: T[]): T[] => {
    if (!roster) return list
    return list.flatMap(s => {
      const found = findRosterSquad(s, roster)
      if (found) return [{ ...s, id: found.id, name: found.name }]
      return s.id ? [] : [s] // Kadrodan silinmiş takım düşer, elle yazılmış ad kalır.
    })
  }, [roster])

  /** Simgenin elle atanmış üyeleri + takımlarının kadrodaki oyuncuları, tekrarsız. */
  const liveMembers = useCallback(<T extends RosterPlayer>(own: T[], list: AssignedSquad[]): RosterPlayer[] => {
    if (!roster) return own
    const merged = new Map<string, RosterPlayer>(own.map(m => [m.id, m]))
    for (const s of list) {
      for (const p of findRosterSquad(s, roster)?.players ?? []) {
        if (!merged.has(p.id)) merged.set(p.id, p)
      }
    }
    return [...merged.values()]
  }, [roster])

  return useMemo(() => ({ liveSquads, liveMembers }), [liveSquads, liveMembers])
}
